/**
 * Morning, afternoon and evening briefs: Samsung "Now Brief"-style summaries, one per part of
 * the day, shown once each and re-openable from the island. Pure so the wording is testable.
 */

export type BriefPeriod = 'morning' | 'afternoon' | 'evening'

export interface BriefCounts {
  open: number
  overdue: number
  dueToday: number
  dueTomorrow: number
  doneToday: number
  doneThisWeek: number
  supportOpen: number
  supportBreached: number
}

export interface BriefRow {
  kind: 'due' | 'overdue' | 'support' | 'done' | 'tomorrow' | 'weather' | 'step' | 'meetings' | 'clear'
  text: string
}

export interface Brief {
  id: string
  period: BriefPeriod
  title: string
  subtitle: string
  rows: BriefRow[]
}

/** null outside waking hours (11pm-5am): nobody wants a brief at 2am. */
export function briefPeriod(date: Date): BriefPeriod | null {
  const h = date.getHours()
  if (h >= 5 && h < 12) return 'morning'
  if (h >= 12 && h < 17) return 'afternoon'
  if (h >= 17 && h < 23) return 'evening'
  return null
}

const n = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`

export function buildBrief(args: {
  period: BriefPeriod
  date: Date
  name?: string
  counts: BriefCounts
  nextStep?: string | null
  weather?: { temperatureC: number; label: string } | null
  topDueToday?: string | null
  /** Remaining meetings today from Google Calendar, if connected. */
  meetings?: { count: number; nextTitle?: string | null; nextTime?: string | null } | null
}): Brief {
  const { period, counts: c, name, nextStep, weather } = args
  const first = name?.split(' ')[0]
  const rows: BriefRow[] = []
  const weatherRow = weather ? { kind: 'weather' as const, text: `${weather.temperatureC}° and ${weather.label.toLowerCase()} outside` } : null

  const m = args.meetings
  const meetingsRow = m && m.count
    ? { kind: 'meetings' as const, text: `${n(m.count, 'meeting')} ${period === 'evening' ? 'left today' : 'today'}${m.nextTitle ? `, next “${m.nextTitle}”${m.nextTime ? ` at ${m.nextTime}` : ''}` : ''}` }
    : null
  if (period === 'morning') {
    if (weatherRow) rows.push(weatherRow)
    if (meetingsRow) rows.push(meetingsRow)
    if (c.dueToday) rows.push({ kind: 'due', text: `${n(c.dueToday, 'item')} due today${args.topDueToday ? `, starting with “${args.topDueToday}”` : ''}` })
    if (c.overdue) rows.push({ kind: 'overdue', text: `${n(c.overdue, 'item')} carried over, overdue` })
    if (c.supportBreached) rows.push({ kind: 'support', text: `${n(c.supportBreached, 'L3 ticket')} breached SLA` })
    else if (c.supportOpen) rows.push({ kind: 'support', text: `${n(c.supportOpen, 'L3 ticket')} on your plate` })
  } else if (period === 'afternoon') {
    rows.push({ kind: 'done', text: c.doneToday ? `${n(c.doneToday, 'item')} closed so far today` : 'Nothing closed yet today' })
    if (meetingsRow) rows.push(meetingsRow)
    if (c.dueToday) rows.push({ kind: 'due', text: `${n(c.dueToday, 'item')} still due before the day ends` })
    if (c.overdue) rows.push({ kind: 'overdue', text: `${n(c.overdue, 'item')} overdue` })
    if (c.supportBreached) rows.push({ kind: 'support', text: `${n(c.supportBreached, 'L3 ticket')} breached SLA` })
    if (weatherRow) rows.push(weatherRow)
  } else {
    rows.push({ kind: 'done', text: c.doneToday ? `You closed ${n(c.doneToday, 'item')} today, ${c.doneThisWeek} this week` : `${n(c.doneThisWeek, 'item')} closed this week` })
    if (c.dueTomorrow) rows.push({ kind: 'tomorrow', text: `${n(c.dueTomorrow, 'item')} due tomorrow` })
    if (c.overdue) rows.push({ kind: 'overdue', text: `${n(c.overdue, 'item')} still overdue` })
    if (c.supportBreached) rows.push({ kind: 'support', text: `${n(c.supportBreached, 'L3 ticket')} breached, worth a look before you log off` })
  }
  if (nextStep && period !== 'evening') rows.push({ kind: 'step', text: nextStep })
  if (!rows.some((r) => r.kind !== 'weather')) rows.push({ kind: 'clear', text: period === 'evening' ? 'All clear. Enjoy your evening.' : 'Nothing urgent. A good time to get ahead.' })

  const title = period === 'morning' ? 'Morning Brief' : period === 'afternoon' ? 'Afternoon Brief' : 'Evening Brief'
  const hello = period === 'morning' ? 'Good morning' : period === 'afternoon' ? 'Good afternoon' : 'Good evening'
  return {
    id: `${args.date.toDateString()}:${period}`,
    period,
    title,
    subtitle: `${hello}${first ? `, ${first}` : ''}. ${period === 'evening' ? 'Here’s how today went.' : 'Here’s your day at a glance.'}`,
    rows: rows.slice(0, 5),
  }
}
